import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  ListItemButton,
  ListItemIcon,
  Breadcrumbs,
  Paper,
  Radio,
  FormControlLabel,
  RadioGroup,
  Checkbox,
  TextField,
  Divider,
} from "@mui/material";
import { Bento, WarningAmber } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { themes, theme } from "services/constants";
import { ThemeProvider } from "@mui/material/styles";
import { approveSidebarWidth } from "pages/TeamZone/Approve/approveUi";
import {
  designColors,
  withDesignFont,
  navRowContainerSx,
  navRowTextSx,
} from "services/constants/designTokens";
import { CustomButton } from "components/CustomComponents";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import { defaultAllSelection } from "services/constants";
import { checkEditorPermission } from "services/utils/HelperFunction";

export const EODSettings = () => {
  const { domain, topbarCompanyId, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );

  // People's real provisioning payload names this module "Settings", not
  // "Feature Configuration" (see Sidebar/index.js's PEOPLE_VIEWER_GATED_MODULES
  // comment).
  const check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Settings",
    "EOD Settings",
  );

  const isAllCompaniesSelected =
    Number(topbarCompanyId) === Number(defaultAllSelection);
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [exists, setExists] = useState(false);
  // EOD Settings is the only screen left here. Check-In Policy and Geo
  // Fenced Sites moved to Team Workspace > Settings.
  const activeScreen = "eod";

  // Old links and bookmarks that deep-linked the moved screens through
  // ?tab= are sent on to their new pages.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = (
      params.get("tab") ||
      params.get("view") ||
      params.get("section") ||
      ""
    ).toLowerCase();

    if (
      tab === "site" ||
      tab === "geo-fenced" ||
      tab === "geofenced" ||
      tab === "geo-fenced-sites"
    ) {
      history.replace("/admin/teamzone/settings/geo-fenced-sites");
    } else if (tab === "policy" || tab === "check-in-policy") {
      history.replace("/admin/teamzone/settings/check-in-policy");
    }
  }, [location.search, location.key, history]);

  const [formData, setFormData] = useState({
    report_type: "basic",
    auto_switch_mode: false,
    submission_deadline: dayjs("2026-02-06T18:00"),
    reminder_method: "in_app",
    reminder_buffer_mins: 30,
  });

  useEffect(() => {
    if (activeScreen !== "eod" || !topbarCompanyId || isAllCompaniesSelected) {
      setLoading(false);
      return;
    }

    const fetchSettings = async () => {
      setLoading(true);

      try {
        const response = await customGetAPI(
          `${domain}eod-settings/?company_id=${topbarCompanyId}`,
        );

        if (
          response.ok &&
          response.responseDetails?.data &&
          Object.keys(response.responseDetails.data).length > 0
        ) {
          const fetched = response.responseDetails.data;
          setFormData({
            report_type: fetched.report_type || "basic",
            auto_switch_mode: fetched.auto_switch_mode || false,
            submission_deadline: dayjs(
              `2026-01-01T${fetched.submission_deadline || "18:00"}`,
            ),
            reminder_method: fetched.reminder_method || "in_app",
            reminder_buffer_mins: fetched.reminder_buffer_mins ?? 30,
          });
          setExists(true);
          setLoading(false);
        } else {
          setExists(false);
          setLoading(false);
        }
      } catch (err) {
        console.error("Fetch error", err);
        setExists(false);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [activeScreen, topbarCompanyId, domain, isAllCompaniesSelected]);

  const handleSave = async () => {
    if (isAllCompaniesSelected) {
      showToast("error", "Action not allowed for 'All Companies' view.");
      return;
    }

    if (!check_access) {
      showToast(
        "error",
        "Access denied: You are not authorized to update EOD Settings",
      );
      return;
    }

    setSaveLoading(true);
    const payload = {
      company_id: topbarCompanyId,
      report_type: formData.report_type,
      auto_switch_mode: formData.auto_switch_mode,
      submission_deadline: formData.submission_deadline.format("HH:mm"),
      reminder_method: formData.reminder_method,
    };

    if (formData.report_type === "project") {
      payload.reminder_buffer_mins = Number(formData.reminder_buffer_mins);
    }

    try {
      const method = exists ? "PUT" : "POST";
      const response = await customAPI(
        `${domain}eod-settings/`,
        method,
        payload,
      );

      if (response.ok) {
        showToast(
          "success",
          `Settings ${exists ? "updated" : "created"} successfully!`,
        );
        setExists(true);
      } else {
        showToast("error", "Failed to save settings. Please try again.");
      }
    } catch (err) {
      console.error('[Index] operation failed:', err);
      showToast("error", "An error occurred while saving.");
    } finally {
      setSaveLoading(false);
    }
  };

  const cardStyle = (active) => ({
    border: `1.5px solid ${active ? "#00D4AA" : "#e0e0e0"}`,
    borderRadius: "10px",
    p: 3,
    mb: 2,
    cursor: "pointer",
    backgroundColor: active ? "#f6fffb" : "white",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "flex-start",
    width: "100%",
  });

  const navItemSx = (key) => ({
    ...navRowContainerSx({ active: activeScreen === key }),
    alignItems: "flex-start",
    p: 1.5,
    mb: 0.5,
  });
  const navIconSx = (key) => ({
    minWidth: 40,
    color: activeScreen === key ? designColors.tealDark : designColors.slate400,
  });
  const navLabelSx = (key) => navRowTextSx({ active: activeScreen === key });

  return (
    // withDesignFont (not the bare designTheme) keeps this screen's existing
    // palette — CustomButton below may rely on
    // it — while still lining the sidebar font up with Team Zone/My Zone.
    <ThemeProvider theme={withDesignFont(theme)}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            display: "flex",
            height: "95vh",
            backgroundColor: "#f5f5fd",
            overflow: "hidden",
          }}
        >
          {/* Sidebar — same width/selected-row treatment as the Team Zone >
              Approve and My Zone > My Requests rails (approveSidebarWidth,
              navRowContainerSx/navRowTextSx in designTokens.js) so all three
              in-page nav rails read as the same control. */}
          <Box
            sx={{
              width: approveSidebarWidth,
              flexShrink: 0,
              backgroundColor: designColors.white,
              borderRight: `1px solid ${designColors.slate200}`,
              p: 2,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, color: designColors.tealDark, mt: 2, mb: 2, px: 1 }}
            >
              Settings
            </Typography>
            <ListItemButton sx={navItemSx("eod")}>
              <ListItemIcon sx={navIconSx("eod")}>
                <Bento />
              </ListItemIcon>
              <Box>
                <Typography sx={navLabelSx("eod")}>EOD Settings</Typography>
                <Typography variant="caption" sx={{ color: designColors.slate500 }}>
                  Define EOD settings
                </Typography>
              </Box>
            </ListItemButton>
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              overflow: "hidden",
              p: 3,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box>
              <Breadcrumbs aria-label="breadcrumb" separator="›" sx={{ mb: 2 }}>
                <Typography variant="body2">People</Typography>
                <Typography variant="body2">Settings</Typography>
                <Typography variant="body2" color={themes?.blueColor}>
                  EOD Settings
                </Typography>
              </Breadcrumbs>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  mb: 1,
                }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    EOD Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure EOD reporting format, deadline, and reminders
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                overflow: "auto",
              }}
            >
              {activeScreen === "eod" && isAllCompaniesSelected ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 6,
                    borderRadius: "16px",
                    border: "1px dashed #00D4AA",
                    textAlign: "center",
                    backgroundColor: "white",
                    mt: 0,
                  }}
                >
                  <WarningAmber
                    sx={{ fontSize: 80, color: "#00D4AA", mb: 2 }}
                  />
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    Company Selection Required
                  </Typography>
                  <Typography color="text.secondary" variant="body1">
                    EOD Settings cannot be configured in the{" "}
                    <b>'All Companies'</b> view. Please select a specific
                    company from the top bar to continue.
                  </Typography>
                </Paper>
              ) : activeScreen === "eod" && loading ? (
                <Loader info="Loading..." />
              ) : (
                activeScreen === "eod" && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 3, md: 4 },
                      borderRadius: "16px",
                      border: "1px solid #eef0f2",
                      backgroundColor: "white",
                      mt: 0,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                      EOD Report Settings
                    </Typography>

                    <Typography sx={{ fontWeight: 600, mb: 2 }}>
                      EOD Report Type
                    </Typography>
                    <RadioGroup
                      value={formData.report_type}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          report_type: e.target.value,
                        })
                      }
                    >
                      <Box
                        sx={cardStyle(formData.report_type === "basic")}
                        onClick={() =>
                          setFormData({ ...formData, report_type: "basic" })
                        }
                      >
                        <Radio
                          value="basic"
                          color="success"
                          sx={{ mt: -0.5 }}
                        />
                        <Box sx={{ ml: 1 }}>
                          <Typography sx={{ fontWeight: 700 }}>
                            Basic EOD
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Simple reporting format. Employees manually list
                            tasks.
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={cardStyle(formData.report_type === "project")}
                        onClick={() =>
                          setFormData({ ...formData, report_type: "project" })
                        }
                      >
                        <Radio
                          value="project"
                          color="success"
                          sx={{ mt: -0.5 }}
                        />
                        <Box sx={{ ml: 1, width: "100%" }}>
                          <Typography sx={{ fontWeight: 700 }}>
                            Project-Linked EOD
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Auto-syncs with tasks from the project management
                            module.
                          </Typography>
                          {formData.report_type === "project" && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.auto_switch_mode}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      auto_switch_mode: e.target.checked,
                                    })
                                  }
                                  color="success"
                                  size="small"
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  Auto-detect and switch mode based on project
                                  assignment.
                                </Typography>
                              }
                              sx={{ mt: 2, ml: 0 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </RadioGroup>

                    <Divider sx={{ my: 4 }} />

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                      Notification & Compliance
                    </Typography>

                    {/* Deadline Row */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { md: "center" },
                        mb: 3,
                        gap: 4,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 600 }}>
                          EOD Submission Deadline
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          The cutoff time after which reports are marked late.
                        </Typography>
                      </Box>
                      <TimePicker
                        value={formData.submission_deadline}
                        onChange={(val) =>
                          setFormData({ ...formData, submission_deadline: val })
                        }
                        sx={{ width: { xs: "100%", md: 320 } }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { md: "center" },
                        mb: 3,
                        gap: 4,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 600 }}>
                          Reminder Method
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Choose the notification channel.
                        </Typography>
                      </Box>
                      <RadioGroup
                        row
                        value={formData.reminder_method}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            reminder_method: e.target.value,
                          })
                        }
                        sx={{ width: { xs: "100%", md: 320 } }}
                      >
                        <FormControlLabel
                          value="in_app"
                          control={<Radio color="success" size="small" />}
                          label="In-App"
                        />
                        <FormControlLabel
                          value="email"
                          control={<Radio color="success" size="small" />}
                          label="Email"
                        />
                        <FormControlLabel
                          value="both"
                          control={<Radio color="success" size="small" />}
                          label="Both"
                        />
                      </RadioGroup>
                    </Box>

                    {formData.report_type === "project" && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          alignItems: { md: "center" },
                          mb: 3,
                          gap: 4,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 600 }}>
                            Send Reminder Before Deadline
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Minutes before deadline to notify.
                          </Typography>
                        </Box>
                        <TextField
                          type="number"
                          inputProps={{ min: 0 }}
                          value={formData.reminder_buffer_mins}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              reminder_buffer_mins: e.target.value,
                            })
                          }
                          placeholder="e.g. 45"
                          helperText={
                            '0 = notify exactly at the deadline, not "no reminder"'
                          }
                          sx={{ width: { xs: "100%", md: 320 } }}
                        />
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 3,
                      }}
                    >
                      <CustomButton
                        actionFuntion={handleSave}
                        actionName={saveLoading ? "Saving..." : "Save"}
                        typeName="button"
                        disableAction={saveLoading}
                      />
                    </Box>
                  </Paper>
                )
              )}
            </Box>
          </Box>
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
};
